/**
 * Open-redirect guard: only allow a SAME-ORIGIN RELATIVE path. Rejects
 * absolute URLs (http(s)://...), protocol-relative URLs (//host) and anything
 * that does not resolve to this origin — WHATWG URL parsing treats
 * backslashes like slashes, so variants like /\evil.com are caught by the
 * origin comparison even though they pass the prefix checks.
 */
export function safeRelativeRedirect(redirect?: string): string | null {
  if (!redirect) return null
  // Must be a root-relative path and NOT a protocol-relative "//host" target.
  if (!redirect.startsWith('/') || redirect.startsWith('//')) return null
  // Defensive: reject anything that parses as an absolute URL.
  try {
    const parsed = new URL(redirect, window.location.origin)
    if (parsed.origin !== window.location.origin) return null
  } catch {
    return null
  }
  return redirect
}

/**
 * Splits a validated relative redirect for the SPA router. navigate()/
 * redirect() treat `to` as a pathname — a raw '?query' left inside it gets
 * re-appended as search and duplicates the params, and the '#hash' would be
 * silently dropped.
 */
export function toRouterTarget(target: string): {
  to: string
  search: Record<string, string>
  hash?: string
} {
  const url = new URL(target, window.location.origin)
  return {
    to: url.pathname,
    search: Object.fromEntries(url.searchParams),
    hash: url.hash ? url.hash.slice(1) : undefined,
  }
}
